import React, { useEffect, useRef, useCallback } from 'react'
import styles from './styles.module.css'

function Modal({ children = 'hello', show, onClose, noMaxWidth, 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledby }) {
  const contentRef = useRef(null)
  const previousActiveElement = useRef(null)

  // Handle escape key
  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape' && onClose) {
      onClose()
    }

    // Focus trap - Tab key handling
    if (event.key === 'Tab' && contentRef.current) {
      const focusableElements = contentRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      if (event.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          event.preventDefault()
          lastElement?.focus()
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          event.preventDefault()
          firstElement?.focus()
        }
      }
    }
  }, [onClose])

  useEffect(() => {
    if (show) {
      // Store the previously focused element
      previousActiveElement.current = document.activeElement

      // Add keyboard event listener
      document.addEventListener('keydown', handleKeyDown)

      // Focus the first focusable element in the modal
      setTimeout(() => {
        if (contentRef.current) {
          const focusableElement = contentRef.current.querySelector(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
          focusableElement?.focus()
        }
      }, 100)

      // Prevent body scroll
      document.body.style.overflow = 'hidden'
    } else {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''

      // Restore focus to the previously focused element
      if (previousActiveElement.current) {
        previousActiveElement.current.focus()
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [show, handleKeyDown])

  return (
    <div
      className={show ? `${styles.wrapper} ${styles.wrapperShown}` : styles.wrapper}
      role="dialog"
      aria-modal={show ? 'true' : undefined}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      aria-hidden={!show}
    >
      {show && (
        <div ref={contentRef} className={noMaxWidth ? `${styles.content} ${styles.contentNoMaxWidth}` : styles.content}>
          {children}
        </div>
      )}
      <div className={styles.background} onClick={onClose} aria-hidden="true" />
    </div>
  )
}

export default Modal
